import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button, Input, Modal, Badge, DarkModeToggleCompact } from '../../components/ui'
import { ShieldCheck, Plus, Trash2, Mail, Users, ArrowLeft, Info, UserCheck, Shield, QrCode, Copy, ExternalLink, Download, Sparkles, Smartphone, Eye } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'

export const AccessPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    const [admins, setAdmins] = useState([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [newEmail, setNewEmail] = useState('')
    const [newRole, setNewRole] = useState('editor')
    const [adding, setAdding] = useState(false)
    const [currentUserEmail, setCurrentUserEmail] = useState('')
    const [activeSubTab, setActiveSubTab] = useState('spectator') // 'spectator' or 'staff'
    const [sportType, setSportType] = useState('goals')
    const [tournamentName, setTournamentName] = useState('')

    useEffect(() => {
        fetchAdmins()
        getCurrentUser()
        fetchTournamentDetails()
    }, [tournamentId])

    const getCurrentUser = async () => {
        const { data: { user } } = await supabase.auth.getUser()
        setCurrentUserEmail(user?.email || '')
    }

    const fetchTournamentDetails = async () => {
        try {
            const { data } = await supabase
                .from('tournaments')
                .select('name, sport_type')
                .eq('id', tournamentId)
                .single()
            if (data) {
                setTournamentName(data.name)
                setSportType(data.sport_type || 'goals')
            }
        } catch (err) {
            console.error('Error fetching tournament details:', err)
        }
    }

    const fetchAdmins = async () => {
        setLoading(true)
        try {
            const { data, error } = await supabase
                .from('tournament_access')
                .select('*')
                .eq('tournament_id', tournamentId)
                .order('created_at', { ascending: false })

            if (error) {
                if (error.code === '42P01') {
                    console.warn('Table tournament_access does not exist yet.')
                } else {
                    throw error
                }
            }
            setAdmins(data || [])
        } catch (err) {
            console.error('Error fetching admins:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleAddAdmin = async (e) => {
        e.preventDefault()
        setAdding(true)
        try {
            const { error } = await supabase
                .from('tournament_access')
                .insert({
                    tournament_id: tournamentId,
                    email: newEmail.trim().toLowerCase(),
                    role: newRole
                })

            if (error) {
                if (error.code === '23505') throw new Error(t('access.error_duplicate'))
                throw error
            }

            setNewEmail('')
            setShowModal(false)
            fetchAdmins()
            alert(t('access.alert_invite_sent'))
        } catch (err) {
            alert(t('access.label_error') + ': ' + err.message)
        } finally {
            setAdding(false)
        }
    }

    const handleRemoveAdmin = async (id) => {
        if (!confirm(t('access.confirm_revoke'))) return
        try {
            const { error } = await supabase
                .from('tournament_access')
                .delete()
                .eq('id', id)

            if (error) throw error
            fetchAdmins()
        } catch (err) {
            alert(t('access.label_error') + ': ' + err.message)
        }
    }

    const isEsports = sportType === 'esports'
    const publicLink = `${window.location.protocol}//${window.location.host}/live/${tournamentId}`

    const copyPublicLink = () => {
        navigator.clipboard.writeText(publicLink)
        alert('Copied to clipboard!')
    }

    const downloadQRCode = () => {
        const svg = document.getElementById('tournament-qr-code')
        if (!svg) return
        const svgString = new XMLSerializer().serializeToString(svg)
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
        const URL = window.URL || window.webkitURL || window
        const blobURL = URL.createObjectURL(svgBlob)
        const image = new Image()
        image.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = 400
            canvas.height = 400
            const context = canvas.getContext('2d')
            context.fillStyle = '#FFFFFF'
            context.fillRect(0, 0, 400, 400)
            context.drawImage(image, 0, 0, 400, 400)
            const png = canvas.toDataURL('image/png')
            const downloadLink = document.createElement('a')
            downloadLink.href = png
            downloadLink.download = `${tournamentName.replace(/\s+/g, '_')}_QR_Code.png`
            document.body.appendChild(downloadLink)
            downloadLink.click()
            document.body.removeChild(downloadLink)
        }
        image.src = blobURL
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container animate-fadeIn">
                {/* Hero Section */}
                <div className="mb-8 animate-slideUp">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/admin/dashboard')} 
                        className={`mb-4 ${isEsports ? 'text-slate-300 hover:text-white hover:bg-white/5' : ''}`}
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        {t('access.btn_back')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className={`p-3.5 rounded-2xl shadow-lg transition-all duration-500 hover:rotate-12 ${
                                isEsports 
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                                    : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_20px_rgba(79,70,229,0.3)]'
                            }`}>
                                <ShieldCheck className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-5xl font-bold leading-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    {t('access.title')}
                                </h1>
                                <p className={`text-lg mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                                    Beheer de toegang voor zowel toeschouwers als mede-organisatoren.
                                </p>
                            </div>
                        </div>

                        <DarkModeToggleCompact />
                    </div>

                    {/* Sub Tabs */}
                    <div className="flex gap-4 border-b border-gray-200/20 pb-1 mb-6">
                        <button
                            onClick={() => setActiveSubTab('spectator')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeSubTab === 'spectator' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <QrCode size={18} />
                            Toeschouwers & QR-Code
                        </button>
                        <button
                            onClick={() => setActiveSubTab('staff')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeSubTab === 'staff' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Shield size={18} />
                            Beheerders & Rechten
                        </button>
                    </div>
                </div>

                {/* Sub Tab: SPECTATOR QR */}
                {activeSubTab === 'spectator' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-slideUp">
                        {/* QR Panel */}
                        <div className={`glass-strong rounded-3xl p-8 border shadow-xl flex flex-col items-center text-center ${
                            isEsports ? 'bg-purple-950/10 border-purple-500/20 shadow-[0_0_35px_rgba(168,85,247,0.06)]' : 'bg-white border-gray-100'
                        }`}>
                            <div className="w-full flex items-center justify-between mb-6 pb-4 border-b border-gray-200/10 text-left">
                                <div>
                                    <h3 className={`text-xl font-bold ${isEsports ? 'text-white' : 'text-gray-900'}`}>Toernooi QR Code</h3>
                                    <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Toeschouwers scannen deze code voor live uitslagen.</p>
                                </div>
                                <Sparkles className={isEsports ? 'text-purple-400' : 'text-indigo-500'} size={24} />
                            </div>

                            {/* Beautiful QR Canvas Frame */}
                            <div className={`p-6 rounded-3xl border shadow-inner mb-6 transition-all duration-300 hover:scale-102 ${
                                isEsports 
                                    ? 'bg-[#0f0b18] border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.1)]' 
                                    : 'bg-gray-50 border-gray-100'
                            }`}>
                                <QRCodeSVG 
                                    id="tournament-qr-code"
                                    value={publicLink} 
                                    size={220} 
                                    level="H" 
                                    includeMargin={true}
                                    className="rounded-2xl"
                                />
                            </div>

                            {/* Public Link Copy Area */}
                            <div className="w-full mb-6">
                                <label className={`block text-xs font-bold text-left mb-2 uppercase tracking-wide ${
                                    isEsports ? 'text-purple-300' : 'text-gray-500'
                                }`}>
                                    Publieke Live Link
                                </label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        readOnly 
                                        value={publicLink}
                                        className={`flex-1 text-sm rounded-xl px-4 py-3 border transition-all ${
                                            isEsports 
                                                ? 'bg-purple-950/20 border-purple-500/25 text-white font-mono' 
                                                : 'bg-gray-50 border-gray-200 font-mono text-gray-700'
                                        }`}
                                    />
                                    <Button onClick={copyPublicLink} variant="ghost" className="p-3.5 rounded-xl border border-gray-200/20">
                                        <Copy size={18} />
                                    </Button>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="grid grid-cols-2 gap-4 w-full">
                                <Button 
                                    onClick={downloadQRCode} 
                                    variant="primary" 
                                    className={`py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold ${
                                        isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]' : ''
                                    }`}
                                >
                                    <Download size={18} /> Download QR
                                </Button>
                                <Button 
                                    onClick={() => window.open(publicLink, '_blank')} 
                                    variant="ghost" 
                                    className="py-3.5 rounded-xl border border-gray-200/20 flex items-center justify-center gap-2 font-bold"
                                >
                                    <ExternalLink size={18} /> Live Bekijken
                                </Button>
                            </div>
                        </div>

                        {/* Spectator Mockup View */}
                        <div className={`glass-strong rounded-3xl p-8 border shadow-xl flex flex-col justify-between ${
                            isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                        }`}>
                            <div className="mb-6">
                                <h3 className={`text-xl font-bold flex items-center gap-2.5 ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    <Smartphone className={isEsports ? 'text-purple-400' : 'text-indigo-500'} size={24} />
                                    Toeschouwers Live App
                                </h3>
                                <p className={`text-xs mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                    Zo ervaren bezoekers en spelers je toernooi op hun eigen mobiele telefoon.
                                </p>
                            </div>

                            {/* Mobile Mockup Layout */}
                            <div className={`flex-1 rounded-[2rem] border-4 p-4 min-h-[300px] flex flex-col relative overflow-hidden transition-all duration-500 hover:shadow-2xl ${
                                isEsports 
                                    ? 'bg-[#0a0712] border-purple-500/30 shadow-[0_0_40px_rgba(168,85,247,0.08)]' 
                                    : 'bg-slate-900 border-slate-950 shadow-lg'
                            }`}>
                                {/* Notch */}
                                <div className="w-24 h-4 bg-black rounded-full mx-auto mb-4 relative z-20"></div>

                                {/* Mock Header */}
                                <div className="flex justify-between items-center mb-4 shrink-0 px-2">
                                    <span className="text-[10px] font-black tracking-widest uppercase text-yellow-400">🏆 {tournamentName || 'PROPOULE'}</span>
                                    <span className="text-[9px] bg-red-600 px-2 py-0.5 rounded text-white font-bold animate-pulse">LIVE</span>
                                </div>

                                {/* Mock Content */}
                                <div className="space-y-3 flex-1 overflow-hidden px-1">
                                    <div className="bg-white/5 border border-white/10 p-3 rounded-xl">
                                        <span className="text-[8px] text-gray-400 uppercase tracking-widest block mb-1">Volgende wedstrijd</span>
                                        <div className="flex justify-between items-center text-xs font-bold text-white">
                                            <span>Team Alpha</span>
                                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[9px]">16:30</span>
                                            <span>Team Omega</span>
                                        </div>
                                    </div>

                                    <div className="bg-white/5 border border-white/10 p-3 rounded-xl">
                                        <span className="text-[8px] text-gray-400 uppercase tracking-widest block mb-1">Koploper Poule A</span>
                                        <div className="flex items-center justify-between text-xs font-bold text-white">
                                            <div className="flex items-center gap-2">
                                                <span className="text-yellow-400">1.</span>
                                                <span>Faze Clan</span>
                                            </div>
                                            <span className="text-cyan-400">9 Punten</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Screen Overlay Glow */}
                                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-purple-500/20 to-transparent pointer-events-none z-10"></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sub Tab: STAFF & ADMIN ACCESS */}
                {activeSubTab === 'staff' && (
                    <div className={`glass-strong rounded-3xl p-6 sm:p-8 animate-slideUp border shadow-xl ${
                        isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                    }`}>
                        <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
                            <div>
                                <h2 className={`text-xl font-bold flex items-center gap-2 ${isEsports ? 'text-white' : 'text-gray-800'}`}>
                                    <Users className={isEsports ? 'text-purple-400' : 'text-indigo-500'} />
                                    Mede-organisatoren & Beheerders
                                </h2>
                                <p className={`text-sm mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('access.tab_desc')}</p>
                            </div>
                            <Button 
                                onClick={() => setShowModal(true)} 
                                variant="primary" 
                                className={`flex items-center gap-2 font-bold ${
                                    isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_20px_rgba(168,85,247,0.35)]' : ''
                                }`}
                            >
                                <Plus size={20} /> {t('access.btn_add')}
                            </Button>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-12 animate-pulse">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Current User Card */}
                                <div className={`flex items-center justify-between p-5 border rounded-2xl transition-all ${
                                    isEsports 
                                        ? 'bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.05)]' 
                                        : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'
                                }`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold shadow-sm ${
                                            isEsports ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-200 text-blue-700'
                                        }`}>
                                            <UserCheck size={20} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-lg">{t('access.label_you_owner')}</div>
                                            <div className={`text-sm ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{currentUserEmail}</div>
                                        </div>
                                    </div>
                                    <Badge variant="neutral" className={isEsports ? 'bg-purple-500/20 text-purple-300 border border-purple-500/20 font-black' : 'bg-blue-200 text-blue-800'}>
                                        OWNER
                                    </Badge>
                                </div>

                                {/* Added Admins */}
                                {admins.length === 0 && (
                                    <div className={`text-center py-16 italic rounded-2xl border-2 border-dashed ${
                                        isEsports ? 'border-purple-500/15 text-slate-400 bg-purple-950/5' : 'border-gray-200 text-gray-400 bg-gray-50/50'
                                    }`}>
                                        {t('access.empty_admins')}
                                    </div>
                                )}

                                {admins.map(admin => (
                                    <div key={admin.id} className={`group flex items-center justify-between p-5 rounded-2xl border transition-all duration-300 hover:scale-[1.01] ${
                                        isEsports 
                                            ? 'bg-purple-950/5 border-purple-500/15 hover:border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.02)]' 
                                            : 'glass-subtle border-gray-100 hover:shadow-md'
                                    }`}>
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-sm ${
                                                isEsports ? 'bg-purple-500/20 text-purple-300' : 'bg-orange-100 text-orange-600'
                                            }`}>
                                                {admin.email.substring(0, 2).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="font-bold text-lg">{admin.email}</div>
                                                <div className={`text-sm flex items-center gap-1.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                                    <Mail size={12} />
                                                    <span className="font-medium capitalize">{admin.role === 'editor' ? t('access.role_editor') : t('access.role_viewer')}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge variant={admin.role === 'editor' ? 'success' : 'neutral'} className={isEsports && admin.role === 'viewer' ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20' : ''}>
                                                {admin.role.toUpperCase()}
                                            </Badge>
                                            <button
                                                onClick={() => handleRemoveAdmin(admin.id)}
                                                className={`p-2.5 rounded-xl transition-all opacity-0 group-hover:opacity-100 ${
                                                    isEsports ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                                                }`}
                                                title={t('access.tooltip_revoke')}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className={`mt-8 p-5 rounded-2xl border flex gap-3 text-sm shadow-sm transition-colors ${
                            isEsports 
                                ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-300 shadow-[0_0_15px_rgba(234,179,8,0.05)]' 
                                : 'bg-yellow-50 border-yellow-100 text-yellow-800'
                        }`}>
                            <Info className={`shrink-0 ${isEsports ? 'text-yellow-400' : 'text-yellow-600'}`} size={20} />
                            <div>
                                <strong className={`block mb-0.5 ${isEsports ? 'text-yellow-200' : 'text-yellow-900'}`}>{t('access.warning_label')}</strong> 
                                {t('access.warning_text')}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Modal */}
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('access.modal_title')}>
                <form onSubmit={handleAddAdmin} className="space-y-6">
                    <div>
                        <Input
                            label={t('access.label_email')}
                            type="email"
                            required
                            placeholder="naam@voorbeeld.nl"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">{t('access.label_role_rights')}</label>
                        <div className="grid grid-cols-2 gap-4">
                            <label className={`cursor-pointer p-4 border-2 rounded-xl flex flex-col items-center gap-3 transition-all hover:bg-gray-50 ${newRole === 'editor' ? 'border-[var(--primary)] bg-orange-50/50 text-[var(--primary)]' : 'border-gray-100'}`}>
                                <input type="radio" name="role" value="editor" checked={newRole === 'editor'} onChange={() => setNewRole('editor')} className="hidden" />
                                <div className={`p-2 rounded-full ${newRole === 'editor' ? 'bg-[var(--primary)] text-white' : 'bg-gray-100 text-gray-400'}`}>
                                    <ShieldCheck size={24} />
                                </div>
                                <div className="text-center">
                                    <span className="font-bold text-sm block">{t('access.editor_title')}</span>
                                    <span className="text-[10px] text-gray-500 leading-tight block mt-1">{t('access.editor_desc')}</span>
                                </div>
                            </label>

                            <label className={`cursor-pointer p-4 border-2 rounded-xl flex flex-col items-center gap-3 transition-all hover:bg-gray-50 ${newRole === 'viewer' ? 'border-[var(--primary)] bg-orange-50/50 text-[var(--primary)]' : 'border-gray-100'}`}>
                                <input type="radio" name="role" value="viewer" checked={newRole === 'viewer'} onChange={() => setNewRole('viewer')} className="hidden" />
                                <div className={`p-2 rounded-full ${newRole === 'viewer' ? 'bg-[var(--primary)] text-white' : 'bg-gray-100 text-gray-400'}`}>
                                    <Shield size={24} />
                                </div>
                                <div className="text-center">
                                    <span className="font-bold text-sm block">{t('access.viewer_title')}</span>
                                    <span className="text-[10px] text-gray-500 leading-tight block mt-1">{t('access.viewer_desc')}</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="flex-1">
                            {t('access.btn_cancel')}
                        </Button>
                        <Button type="submit" disabled={adding} variant="primary" className="flex-1">
                            {adding ? t('access.btn_adding') : t('access.btn_invite')}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    )
}
